import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// A deploy replaces the hashed assets, so a tab still running the previous
// build starts failing its lazy chunk/style loads ("Refused to apply style …
// MIME type ('text/html')" — the 404 falls through to the SPA's HTML). Vite
// reports exactly that as `vite:preloadError`; reload once to pick up the new
// build. The timestamp guard stops a loop if the reload lands on a broken
// deploy that still can't serve the chunk.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "sphinx:preload-reload-at";
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY) ?? 0);
  } catch {
    /* storage unavailable — reload anyway, the guard is best-effort */
  }
  if (Date.now() - last < 30_000) return; // let Vite surface the error instead
  event.preventDefault();
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  console.warn("[sphinx] assets changed under this tab (new deploy) — reloading");
  window.location.reload();
});
import { AppRouterProvider } from "./router";
import "./index.css";
import { reportCaught } from "./lib/error-reporting";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { IframeRouterError } from "./components/IframeRouterError.jsx";
import { initializeSessionToken } from "./lib/session-token.js";
import { adoptAccessKeyFromUrl } from "./lib/sphinx-access-key";
import { SphinxAuthGate } from "./components/sphinx-sign-in";
import { HOSTED_MODE, SANDBOX_ORIGIN } from "./lib/config";
import {
  isDebugOAuthCallbackPath,
  normalizeInitialLegacyHashBookmark,
} from "./lib/app-navigation";
import OAuthDebugCallback from "./components/oauth/OAuthDebugCallback";
import {
  getInitialThemeMode,
  getInitialThemePreset,
  updateThemeMode,
  updateThemePreset,
} from "./lib/theme-utils";

/**
 * A hosted deploy with no sandbox origin is a SECURITY REGRESSION, not a
 * config nicety.
 *
 * `VITE_SPHINX_SANDBOX_ORIGIN` is what puts MCP Apps widgets on an origin that
 * shares no cookies with the host app. Unset, the iframe falls back to
 * same-origin and the isolation the sandbox exists to provide is simply gone.
 *
 * Deliberately non-fatal: refusing to start would take the whole app down over
 * a widget-isolation setting, which is a worse outcome than a loud deploy.
 *
 * SET TO THE APP'S OWN ORIGIN counts as unset. A configured value that equals
 * `window.location.origin` produces exactly the same same-origin iframe as no
 * value at all — the isolation is gone either way — and it is the more likely
 * mistake of the two, because it looks configured.
 */
const sandboxOriginFault =
  HOSTED_MODE && (!SANDBOX_ORIGIN || SANDBOX_ORIGIN === window.location.origin);
if (sandboxOriginFault) {
  const message = SANDBOX_ORIGIN
    ? `VITE_SPHINX_SANDBOX_ORIGIN is set to this app's own origin (${SANDBOX_ORIGIN}). MCP Apps widgets will render SAME-ORIGIN with the host app, losing the cookie/storage isolation the sandbox provides.`
    : "VITE_SPHINX_SANDBOX_ORIGIN is not configured in hosted mode. MCP Apps widgets will render SAME-ORIGIN with the host app, losing the cookie/storage isolation the sandbox provides.";
  console.error(`[Sphinx] ${message}`);
}

// Detect if we're inside an iframe - this happens when a user's app uses BrowserRouter
// and does history.pushState, then the iframe is refreshed. The server doesn't recognize
// the new path and serves the Inspector's index.html inside the iframe.
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin restrictions, we're in an iframe
    return true;
  }
})();

// If we're in an iframe, render a helpful error message instead of the full Inspector
if (isInIframe) {
  const root = createRoot(document.getElementById("root")!);
  root.render(
    <StrictMode>
      <IframeRouterError />
    </StrictMode>
  );
} else if (isDebugOAuthCallbackPath(window.location.pathname)) {
  // Throwaway popup for the OAuth debugger. App's theme bootstrap doesn't run
  // here, so apply the stored theme directly.
  updateThemeMode(getInitialThemeMode());
  updateThemePreset(getInitialThemePreset());
  const root = createRoot(document.getElementById("root")!);
  root.render(
    <StrictMode>
      <OAuthDebugCallback />
    </StrictMode>
  );
} else {
  // Sphinx auth is the Yarn sign-in session cookie (components/sphinx-sign-in.tsx,
  // lib/sphinx-auth.ts); data/user.ts stays a static operator. A `?access_key=`
  // in the URL is the service-account escape hatch (lib/sphinx-access-key.ts).
  adoptAccessKeyFromUrl();
  normalizeInitialLegacyHashBookmark();

  // Async bootstrap to initialize the local Hono session token before rendering
  async function bootstrap() {
    const root = createRoot(document.getElementById("root")!);
    const skipLocalSessionBootstrap =
      import.meta.env.DEV && window.location.pathname.startsWith("/__e2e/");

    try {
      if (!HOSTED_MODE && !skipLocalSessionBootstrap) {
        // Initialize session token BEFORE rendering in local mode.
        await initializeSessionToken();
        console.log("[Auth] Session token initialized");
      } else {
        console.log(
          "[Auth] Hosted mode active, skipping session token bootstrap"
        );
      }
    } catch (error) {
      console.error("[Auth] Failed to initialize session token:", error);
      // This branch replaces the whole app with a static screen — without a
      // report the failure is invisible outside the user's own console.
      reportCaught(error, { source: "session_token_bootstrap" });
      // Show error UI instead of crashing
      root.render(
        <StrictMode>
          <div
            style={{
              padding: "2rem",
              textAlign: "center",
              fontFamily: "system-ui",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
            }}
          >
            <img
              src="/sphinx.svg"
              alt="Sphinx"
              style={{ width: "120px", height: "auto", marginBottom: "1.5rem" }}
            />
            <h1 style={{ color: "#dc2626", marginBottom: "0.5rem" }}>
              Session Error
            </h1>
            <p style={{ marginBottom: "0.25rem" }}>
              Failed to establish secure session.
            </p>
            <p style={{ color: "#666", fontSize: "0.875rem" }}>
              If accessing via network, use localhost instead.
            </p>
            <button
              onClick={() => location.reload()}
              style={{
                marginTop: "1.5rem",
                padding: "0.75rem 1.5rem",
                cursor: "pointer",
                backgroundColor: "#18181b",
                color: "#fff",
                border: "none",
                borderRadius: "0.5rem",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              Restart App
            </button>
          </div>
        </StrictMode>
      );
      return;
    }

    root.render(
      <StrictMode>
        <ErrorBoundary name="root">
          <SphinxAuthGate>
            <AppRouterProvider />
          </SphinxAuthGate>
        </ErrorBoundary>
      </StrictMode>
    );
  }

  bootstrap();
}
